import { useState, useMemo, useCallback } from 'react'
import { useToast } from '@winnin/winnin_ui.components.toast'

import { useAuth } from 'contexts/AuthContext'
import { useDashboardMutations } from 'hooks-queries/dashboards'
import { useTranslate } from 'lib/tolgee'
import { PanelStore } from 'stores'
import { analyticsTracker, TOLGEE_GLOBALS } from 'utils'

import { DUPLICATE_WORKSPACE_DASHBOARD_ERROR_LOG } from './transfer-dashboard-modal.consts'
import { translations } from './transfer-dashboard-modal.i18n'
import type {
  IDuplicateWorkspaceDashboardOption,
  ITransferDashboardModalReturn,
  ITransferDashboardModal,
} from './transfer-dashboard-modal.types'

const { DASHBOARD_TRANSFER } = analyticsTracker.eventNames

export const useTransferDashboardModel = ({
  isOpen,
  handleOpen,
  jobId,
}: ITransferDashboardModal): ITransferDashboardModalReturn => {
  const [workspace, setWorkspace] = useState<IDuplicateWorkspaceDashboardOption | null>(null)
  const t = useTranslate()
  const { createToast } = useToast()

  const { me } = useAuth()

  const workspaceId = Object(PanelStore?.selectedPanel)?.panelId

  const workspacesOptions = useMemo<IDuplicateWorkspaceDashboardOption[]>(() => {
    if (!me?.me?.brands) return []

    return me.me.brands
      .filter(brand => brand.id !== workspaceId)
      .map(brand => ({
        id: brand.id,
        name: brand.name,
        label: brand.name,
        value: brand.id,
        company: brand.company,
      }))
  }, [me, workspaceId])

  const [duplicateWorkspaceDashboard, { loading }] = useDashboardMutations({
    mutationName: 'duplicateWorkspaceDashboard',
    errorLog: DUPLICATE_WORKSPACE_DASHBOARD_ERROR_LOG,
  })

  const handleClose = useCallback(() => {
    handleOpen(false)
  }, [handleOpen])

  const handleChange = (selectedOption: IDuplicateWorkspaceDashboardOption | null) => {
    setWorkspace(selectedOption)
  }

  const handleSubmit = useCallback(() => {
    if (!workspace || workspace.id === workspaceId) return

    const originWorkspace = me?.me?.brands?.find(brand => brand.id === workspaceId) || { name: '' }

    duplicateWorkspaceDashboard({
      variables: {
        duplicateWorkspaceJobInput: {
          workspaceJobId: jobId,
          targetWorkspace: workspace.id.toString(),
        },
      },
    })
      .then(_response => {
        analyticsTracker.eventTrack(DASHBOARD_TRANSFER.TRANSFER_DASHBOARD_SUCCESS, {
          dashboardId: jobId,
          destinationWorkspaceName: workspace.name,
          originWorkspaceName: originWorkspace?.name || '',
        })
        createToast({
          content: t(translations.successfullyTransferredDashboard),
        })
        handleClose()
      })
      .catch(error => {
        analyticsTracker.eventTrack(DASHBOARD_TRANSFER.TRANSFER_DASHBOARD_ERROR, {
          dashboardId: jobId,
          destinationWorkspaceName: workspace.name,
          originWorkspaceName: originWorkspace?.name || '',
        })

        const errorMessages = {
          unauthorized: translations.unAuthorizedTransferDashboard,
          NOT_FOUND: translations.dashboardNotFound,
          'report-job-cant-be-duplicated': translations.reportJobCantBeDuplicated,
        }

        const messageKey = errorMessages[error?.message] || TOLGEE_GLOBALS.somethingWentWrong

        createToast({ content: t(messageKey) })
        handleClose()
      })
  }, [workspace, workspaceId, jobId, me?.me?.brands, duplicateWorkspaceDashboard, t, createToast, handleClose])

  return { workspace, isOpen, handleClose, workspacesOptions, handleChange, handleSubmit, loading }
}
